import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UPDATE_DEFAULTS_NOT_SAVED_ERROR } from 'constants/messageConstants';
import {
  clearMOrgAccessCache,
  isForbidden,
  useResolveMOrgId,
} from 'hooks/useResolveMOrgId';
import { MasterDbService } from 'services/MasterDbServices';
import {
  buildMBase,
  buildMWriteBody,
  readDeviceId,
} from 'utils/mDocumentFactory';

import { QUICK_RECORD_CONFIG, QuickRecordKind } from './addRecord';

export interface QuickRecord {
  globalId: string;
  name: string;
}

interface CreateQuickRecordArgs {
  /** Generated once per confirm and reused on retry. */
  id: string;
  name: string;
  /** Target only: the species type picked, if any. */
  speciesType?: { id: string; name: string } | null;
}

/** Adds a timing or target (name, plus an optional species type on a target) to the user's own list (OP-2498). */
export const useCreateMQuickRecord = (kind: QuickRecordKind) => {
  const queryClient = useQueryClient();
  const resolveMOrgId = useResolveMOrgId();
  const config = QUICK_RECORD_CONFIG[kind];

  return useMutation<QuickRecord, Error, CreateQuickRecordArgs>({
    mutationFn: async ({ id, name, speciesType }) => {
      const trimmed = name.trim();

      const attempt = async (retried: boolean): Promise<QuickRecord> => {
        const orgId = await resolveMOrgId();
        try {
          const response = await MasterDbService.CreateMDocument(
            config.collection,
            buildMWriteBody(
              buildMBase({ id, orgId, globalId: id, deviceId: readDeviceId() }),
              speciesType
                ? {
                    name: trimmed,
                    speciesTypeId: speciesType.id,
                    snapSpeciesType: speciesType.name,
                  }
                : { name: trimmed }
            )
          );
          if (!response.data?.length) {
            throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
          }
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [config.mQueryKey] }),
            queryClient.invalidateQueries({
              queryKey: [config.searchQueryKey],
            }),
          ]);
          return { globalId: id, name: trimmed };
        } catch (err) {
          if (isForbidden(err) && !retried) {
            clearMOrgAccessCache(queryClient);
            return attempt(true);
          }
          throw err;
        }
      };
      return attempt(false);
    },
  });
};
